// File: src/components/admin/sync/SyncDetailView.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschrankt)
// All rights reserved.
//
// Issue #233: Admin sync observability — detail view with tabs.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, Chip, Card, CardContent, Grid, Alert,
    CircularProgress, Stack, Tabs, Tab, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, Tooltip, IconButton, TextField,
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    GetApp as DownloadIcon,
    Person as PersonIcon,
    ContentCopy as CopyIcon,
    FiberManualRecord as PulseIcon,
    Search as SearchIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { adminApi, handleAdminApiError } from '../../../services/adminApi';

const STATUS_CONFIG = {
    completed: { color: 'success', label: 'Completed' },
    partial: { color: 'warning', label: 'Partial' },
    failed: { color: 'error', label: 'Failed' },
    in_progress: { color: 'info', label: 'In Progress' },
    cancelled: { color: 'default', label: 'Cancelled' },
};

const CAMPAIGN_STATUS_CONFIG = {
    success: { color: 'success' },
    completed: { color: 'success' },
    failed: { color: 'error' },
    skipped: { color: 'default' },
    in_progress: { color: 'info' },
    pending: { color: 'default' },
};

const formatDuration = (ms) => {
    if (!ms && ms !== 0) return '-';
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainSec = seconds % 60;
    if (minutes < 60) return `${minutes}m ${remainSec}s`;
    const hours = Math.floor(minutes / 60);
    const remainMin = minutes % 60;
    return `${hours}h ${remainMin}m`;
};

const SyncDetailView = () => {
    const { uid, syncId } = useParams();
    const navigate = useNavigate();
    const [sync, setSync] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);

    // Issue #234: Execution logs state
    const [logs, setLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [logsLoaded, setLogsLoaded] = useState(false);
    const [logLevelFilter, setLogLevelFilter] = useState('all');
    const [logSearch, setLogSearch] = useState('');
    const [expandedLogRows, setExpandedLogRows] = useState(new Set());

    const fetchDetail = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await adminApi.getAdminSyncDetail(uid, syncId);
            setSync(data.sync);
        } catch (err) {
            setError(handleAdminApiError(err).message);
        } finally {
            setLoading(false);
        }
    }, [uid, syncId]);

    useEffect(() => {
        fetchDetail();
    }, [fetchDetail]);

    // Auto-refresh for in_progress syncs
    useEffect(() => {
        if (sync?.status === 'in_progress') {
            const interval = setInterval(fetchDetail, 10000);
            return () => clearInterval(interval);
        }
    }, [sync?.status, fetchDetail]);

    const handleDownloadCSV = async () => {
        try {
            const blob = await adminApi.downloadAdminSyncCSV(uid, syncId);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `sync-${syncId}-${uid}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(handleAdminApiError(err).message);
        }
    };

    // Issue #234: Fetch logs when Logs tab is selected
    const fetchLogs = useCallback(async () => {
        if (logsLoaded) return;
        try {
            setLogsLoading(true);
            const data = await adminApi.getAdminSyncLogs(uid, syncId);
            setLogs(data.logs || []);
            setLogsLoaded(true);
        } catch (err) {
            setError(handleAdminApiError(err).message);
        } finally {
            setLogsLoading(false);
        }
    }, [uid, syncId, logsLoaded]);

    useEffect(() => {
        if (activeTab === 4) fetchLogs();
    }, [activeTab, fetchLogs]);

    const handleDownloadLogsCSV = async () => {
        try {
            const blob = await adminApi.downloadAdminSyncLogsCSV(uid, syncId);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `sync-logs-${syncId}-${uid}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(handleAdminApiError(err).message);
        }
    };

    const toggleLogRow = (idx) => {
        setExpandedLogRows(prev => {
            const next = new Set(prev);
            if (next.has(idx)) next.delete(idx);
            else next.add(idx);
            return next;
        });
    };

    if (loading && !sync) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    }

    if (error && !sync) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (!sync) return null;

    const statusCfg = STATUS_CONFIG[sync.status] || { color: 'default', label: sync.status };
    const durationMs = sync.completedAt
        ? new Date(sync.completedAt).getTime() - new Date(sync.startedAt).getTime()
        : Date.now() - new Date(sync.startedAt).getTime();

    // Gather all campaign details for the campaign tab
    const allCampaignDetails = [];
    for (const [platformId, platform] of Object.entries(sync.platforms || {})) {
        for (const op of (platform.operations || [])) {
            for (const cd of (op.campaignDetails || [])) {
                const progressDetail = sync.progress?.campaignDetails?.find(
                    pd => pd.id === cd.campaignId && pd.phase === op.direction
                );
                allCampaignDetails.push({
                    ...cd,
                    platformId,
                    platformName: platform.platformName,
                    direction: op.direction,
                    phase: progressDetail?.phase || op.direction,
                    error: cd.error || progressDetail?.error || '',
                    processedAt: progressDetail?.processedAt || '',
                });
            }
        }
        for (const removal of (platform.removals || [])) {
            allCampaignDetails.push({
                campaignId: removal.campaignId || removal.entityId || '',
                campaignName: removal.campaignName || removal.name || '',
                status: removal.status || '',
                platformId,
                platformName: platform.platformName,
                direction: 'removal',
                phase: 'remove',
                error: removal.error || '',
                processedAt: '',
            });
        }
    }

    // Also include progress campaign details not in operations
    if (sync.progress?.campaignDetails) {
        for (const pd of sync.progress.campaignDetails) {
            const alreadyAdded = allCampaignDetails.some(cd => cd.campaignId === pd.id && cd.phase === pd.phase);
            if (!alreadyAdded) {
                allCampaignDetails.push({
                    campaignId: pd.id,
                    campaignName: pd.name || pd.id,
                    status: pd.status,
                    platformId: pd.platform || '',
                    platformName: pd.platform || '',
                    direction: pd.phase,
                    phase: pd.phase,
                    error: pd.error || '',
                    processedAt: pd.processedAt || '',
                });
            }
        }
    }

    const failedItems = allCampaignDetails.filter(cd => cd.status === 'failed');

    return (
        <Box>
            {/* Back button + header */}
            <Button startIcon={<BackIcon />} onClick={() => navigate('/syncs')} sx={{ mb: 2 }} data-testid="back-to-list">
                Back to Sync List
            </Button>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Header Card */}
            <Card sx={{ mb: 3 }} data-testid="sync-detail-header">
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                                <Typography variant="h5">Sync Record</Typography>
                                <Chip label={statusCfg.label} color={statusCfg.color} data-testid="detail-status-chip" />
                                {sync.scheduled && <Chip label="Scheduled" variant="outlined" size="small" />}
                                {sync.status === 'in_progress' && (
                                    <PulseIcon sx={{ color: 'info.main', animation: 'pulse 1.5s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } } }} data-testid="pulse-indicator" />
                                )}
                            </Stack>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Sync ID: <code>{sync.syncId}</code>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                User: <Typography
                                    component="span"
                                    variant="body2"
                                    sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                    onClick={() => navigate(`/users/${uid}`)}
                                    data-testid="detail-user-email"
                                >
                                    {sync.email || uid}
                                </Typography> ({uid})
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Started: {new Date(sync.startedAt).toLocaleString()}
                                {sync.completedAt && <> | Completed: {new Date(sync.completedAt).toLocaleString()}</>}
                                {' | '}Duration: {formatDuration(durationMs)}
                            </Typography>
                            {sync.status === 'in_progress' && sync.lastHeartbeat && (
                                <Typography variant="body2" color="text.secondary" data-testid="last-heartbeat">
                                    Last Heartbeat: {new Date(sync.lastHeartbeat).toLocaleString()}
                                </Typography>
                            )}
                        </Box>
                        <Stack direction="row" spacing={1}>
                            <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownloadCSV} data-testid="download-csv-btn">
                                Download CSV
                            </Button>
                            <Button variant="outlined" startIcon={<PersonIcon />} onClick={() => navigate(`/users/${uid}`)} data-testid="view-user-btn">
                                View User
                            </Button>
                        </Stack>
                    </Box>

                    {/* Error banner for failed syncs */}
                    {sync.status === 'failed' && sync.errors?.length > 0 && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {sync.errors.map((e, i) => <div key={i}>{e.message}</div>)}
                        </Alert>
                    )}
                </CardContent>
            </Card>

            {/* Tabs */}
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
                <Tab label="Overview" data-testid="tab-overview" />
                <Tab label={`Campaign Details (${allCampaignDetails.length})`} data-testid="tab-campaigns" />
                <Tab label={`Errors (${failedItems.length})`} data-testid="tab-errors" />
                <Tab label="Raw JSON" data-testid="tab-json" />
                <Tab label={`Logs${logsLoaded ? ` (${logs.length})` : ''}`} data-testid="tab-logs" />
            </Tabs>

            {/* Tab: Overview */}
            {activeTab === 0 && (
                <Box data-testid="overview-tab">
                    {/* Summary cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {[
                            { label: 'Campaigns', value: sync.totals?.campaigns || 0 },
                            { label: 'Ad Groups', value: sync.totals?.adGroups || 0 },
                            { label: 'Keywords', value: sync.totals?.keywords || 0 },
                            { label: 'Ads', value: sync.totals?.ads || 0 },
                            { label: 'Conversions', value: sync.totals?.conversionActions || 0 },
                            { label: 'Removals', value: sync.totals?.removals || 0 },
                            { label: 'Errors', value: sync.totals?.errors || 0, color: (sync.totals?.errors || 0) > 0 ? 'error.main' : undefined },
                        ].map((item) => (
                            <Grid item xs={6} sm={3} md key={item.label}>
                                <Card variant="outlined">
                                    <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                                        <Typography variant="h4" color={item.color || 'text.primary'} data-testid={`summary-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
                                            {item.value}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Platform breakdown */}
                    <Typography variant="h6" sx={{ mb: 1 }}>Platform Breakdown</Typography>
                    <Grid container spacing={2}>
                        {Object.entries(sync.platforms || {}).map(([pid, platform]) => (
                            <Grid item xs={12} sm={6} key={pid}>
                                <Card variant="outlined" data-testid={`platform-card-${pid}`}>
                                    <CardContent>
                                        <Typography variant="subtitle1" fontWeight="bold">{platform.platformName || pid}</Typography>
                                        <Typography variant="body2">Operations: {platform.operations?.length || 0}</Typography>
                                        <Typography variant="body2">Removals: {platform.removals?.length || 0}</Typography>
                                        {platform.operations?.map((op, i) => (
                                            <Chip key={i} label={`${op.direction} (${op.status || 'completed'})`} size="small" sx={{ mr: 0.5, mt: 0.5 }} variant="outlined" />
                                        ))}
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}

            {/* Tab: Campaign Details */}
            {activeTab === 1 && (
                <TableContainer component={Paper} data-testid="campaigns-tab">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Campaign</TableCell>
                                <TableCell>Platform</TableCell>
                                <TableCell>Direction</TableCell>
                                <TableCell>Phase</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Error</TableCell>
                                <TableCell>Processed At</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {allCampaignDetails.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <Typography color="text.secondary" sx={{ py: 2 }}>No campaign details available</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                allCampaignDetails.map((cd, idx) => {
                                    const cStatusCfg = CAMPAIGN_STATUS_CONFIG[cd.status] || { color: 'default' };
                                    return (
                                        <TableRow key={idx} data-testid="campaign-row">
                                            <TableCell>
                                                <Typography variant="body2">{cd.campaignName || cd.campaignId}</Typography>
                                                {cd.campaignId && <Typography variant="caption" color="text.secondary">{cd.campaignId}</Typography>}
                                            </TableCell>
                                            <TableCell>{cd.platformName}</TableCell>
                                            <TableCell>{cd.direction}</TableCell>
                                            <TableCell>{cd.phase}</TableCell>
                                            <TableCell>
                                                <Chip label={cd.status || 'unknown'} color={cStatusCfg.color} size="small" sx={{ fontSize: '0.70rem', height: 24 }} />
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 300 }}>
                                                {cd.error && (
                                                    <Tooltip title={cd.error}>
                                                        <Typography variant="body2" color="error" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}>
                                                            {cd.error}
                                                        </Typography>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                            <TableCell>{cd.processedAt ? new Date(cd.processedAt).toLocaleTimeString() : '-'}</TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Tab: Errors */}
            {activeTab === 2 && (
                <Box data-testid="errors-tab">
                    {failedItems.length === 0 ? (
                        <Alert severity="success">No errors in this sync</Alert>
                    ) : (
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Campaign</TableCell>
                                        <TableCell>Platform</TableCell>
                                        <TableCell>Phase</TableCell>
                                        <TableCell>Error Message</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {failedItems.map((item, idx) => (
                                        <TableRow key={idx} data-testid="error-row">
                                            <TableCell>{item.campaignName || item.campaignId}</TableCell>
                                            <TableCell>{item.platformName}</TableCell>
                                            <TableCell>{item.phase}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="error">{item.error}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title="Copy error">
                                                    <IconButton size="small" onClick={() => navigator.clipboard.writeText(item.error)} data-testid="copy-error-btn">
                                                        <CopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {/* Sync-level errors */}
                    {sync.errors?.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>Sync-Level Errors</Typography>
                            {sync.errors.map((e, i) => (
                                <Alert severity="error" key={i} sx={{ mb: 1 }}>
                                    <Typography variant="body2">{e.message}</Typography>
                                    <Typography variant="caption" color="text.secondary">{e.timestamp}</Typography>
                                </Alert>
                            ))}
                        </Box>
                    )}
                </Box>
            )}

            {/* Tab: Raw JSON */}
            {activeTab === 3 && (
                <Card data-testid="json-tab">
                    <CardContent>
                        <TextField
                            multiline
                            fullWidth
                            minRows={20}
                            maxRows={40}
                            value={JSON.stringify(sync, null, 2)}
                            InputProps={{
                                readOnly: true,
                                style: { fontFamily: 'monospace', fontSize: '0.85rem' },
                            }}
                        />
                    </CardContent>
                </Card>
            )}

            {/* Tab: Logs (Issue #234) */}
            {activeTab === 4 && (
                <Box data-testid="logs-tab">
                    {logsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                    ) : logs.length === 0 ? (
                        <Alert severity="info">No execution logs available for this sync</Alert>
                    ) : (
                        <>
                            {/* Log controls */}
                            <Paper sx={{ p: 2, mb: 2 }}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <TextField
                                        select
                                        size="small"
                                        label="Level"
                                        value={logLevelFilter}
                                        onChange={(e) => setLogLevelFilter(e.target.value)}
                                        sx={{ minWidth: 140 }}
                                        SelectProps={{ native: true }}
                                        data-testid="log-level-filter"
                                    >
                                        <option value="all">All Levels</option>
                                        <option value="error">Errors Only</option>
                                        <option value="warn+error">Warnings + Errors</option>
                                        <option value="info">Info Only</option>
                                    </TextField>
                                    <TextField
                                        size="small"
                                        label="Search logs"
                                        value={logSearch}
                                        onChange={(e) => setLogSearch(e.target.value)}
                                        sx={{ flexGrow: 1 }}
                                        InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
                                        data-testid="log-search"
                                    />
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<DownloadIcon />}
                                        onClick={handleDownloadLogsCSV}
                                        data-testid="download-logs-csv-btn"
                                    >
                                        Download Logs CSV
                                    </Button>
                                </Stack>
                            </Paper>

                            {/* Log entries table */}
                            <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
                                <Table size="small" stickyHeader data-testid="logs-table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ width: 30 }}></TableCell>
                                            <TableCell sx={{ width: 180 }}>Timestamp</TableCell>
                                            <TableCell sx={{ width: 80 }}>Level</TableCell>
                                            <TableCell>Message</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {logs
                                            .filter(entry => {
                                                if (logLevelFilter === 'error' && entry.level !== 'error') return false;
                                                if (logLevelFilter === 'warn+error' && entry.level !== 'warn' && entry.level !== 'error') return false;
                                                if (logLevelFilter === 'info' && entry.level !== 'info') return false;
                                                if (logSearch) {
                                                    const searchLower = logSearch.toLowerCase();
                                                    const msgMatch = entry.message?.toLowerCase().includes(searchLower);
                                                    const metaMatch = entry.meta ? JSON.stringify(entry.meta).toLowerCase().includes(searchLower) : false;
                                                    if (!msgMatch && !metaMatch) return false;
                                                }
                                                return true;
                                            })
                                            .map((entry, idx) => {
                                                const levelColor = entry.level === 'error' ? 'error.main'
                                                    : entry.level === 'warn' ? 'warning.main'
                                                    : 'text.secondary';
                                                const isExpanded = expandedLogRows.has(idx);
                                                const hasMeta = entry.meta && Object.keys(entry.meta).length > 0;
                                                return (
                                                    <React.Fragment key={idx}>
                                                        <TableRow
                                                            hover
                                                            sx={{ cursor: hasMeta ? 'pointer' : 'default' }}
                                                            onClick={() => hasMeta && toggleLogRow(idx)}
                                                            data-testid="log-row"
                                                        >
                                                            <TableCell sx={{ py: 0.5 }}>
                                                                {hasMeta && (
                                                                    <IconButton size="small" sx={{ p: 0 }}>
                                                                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                                                    </IconButton>
                                                                )}
                                                            </TableCell>
                                                            <TableCell sx={{ py: 0.5, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                                                {entry.ts ? new Date(entry.ts).toLocaleTimeString(undefined, { hour12: false, fractionalSecondDigits: 3 }) : '-'}
                                                            </TableCell>
                                                            <TableCell sx={{ py: 0.5 }}>
                                                                <Typography variant="body2" sx={{ color: levelColor, fontWeight: entry.level === 'error' ? 'bold' : 'normal', fontSize: '0.80rem' }}>
                                                                    {entry.level?.toUpperCase()}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ py: 0.5, fontFamily: 'monospace', fontSize: '0.80rem', wordBreak: 'break-word' }}>
                                                                {entry.message}
                                                            </TableCell>
                                                        </TableRow>
                                                        {isExpanded && hasMeta && (
                                                            <TableRow data-testid="log-meta-row">
                                                                <TableCell />
                                                                <TableCell colSpan={3} sx={{ py: 1, bgcolor: 'grey.50' }}>
                                                                    <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                                        {JSON.stringify(entry.meta, null, 2)}
                                                                    </pre>
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })
                                        }
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default SyncDetailView;
